import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const PAGE_SIZE = 10;

const Homeright = () => {
  const [selectedTabs, setSelectedTabs] = useState("alerts");
  const [selectedOption, setSelectedOption] = useState("option1");
  const [cases, setCases] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);
  const [accountIds, setAccountIds] = useState([]);
  const [accountIdsLoaded, setAccountIdsLoaded] = useState(false);

  const alertsContainerRef = useRef(null);

  // ========== AUTH / USER (unchanged) ==========
  useEffect(() => {
    let storedLabId = localStorage.getItem('userLabId') || sessionStorage.getItem('userLabId');
    let storedUserType = localStorage.getItem('userType') || sessionStorage.getItem('userType');
    const userDataStr = localStorage.getItem('userData') || sessionStorage.getItem('userData');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        if (!storedLabId && userData.labId) storedLabId = userData.labId;
        if (!storedUserType && userData.userType) storedUserType = userData.userType;
      } catch (e) { }
    }
    if (storedLabId) setLabId(storedLabId);
    if (storedUserType) setUserType(storedUserType);
    else if (storedLabId) setUserType('lab');
    else setUserType('admin');
    setAuthLoaded(true);
  }, []);

  // ========== Fetch ONLY accountIds for lab users (small payload) ==========
  useEffect(() => {
    if (!authLoaded) return;

    if (userType !== 'lab' || !labId) {
      setAccountIdsLoaded(true);
      return;
    }

    const fetchAccountIds = async () => {
      try {
        const labAccountsResponse = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`
        );
        const ids = (labAccountsResponse.data && labAccountsResponse.data.success)
          ? (labAccountsResponse.data.data || []).map(acc => acc.accountId).filter(Boolean)
          : [];
        setAccountIds(ids);
      } catch (e) {
        setAccountIds([]);
      } finally {
        setAccountIdsLoaded(true);
      }
    };
    fetchAccountIds();
  }, [authLoaded, userType, labId]);

  // ========== Fetch first page of alerts from backend ==========
  const fetchAlertsPage = async (skip, append) => {
    try {
      if (append) setLoadingMore(true); else setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('userType', userType || 'admin');
      if (userType === 'lab') params.set('accountIds', accountIds.join(','));
      params.set('skip', skip);
      params.set('limit', PAGE_SIZE);

      const response = await axios.get(
        `https://hms.backend.hiragroupcloud.com/api/cases/alerts?${params.toString()}`
      );

      if (response.data && response.data.success) {
        setCases(prev => append ? [...prev, ...response.data.data] : response.data.data);
        setTotalCount(response.data.total || 0);
      } else {
        setError("Failed to load data. Please try again later.");
      }
    } catch (error) {
      setError("Failed to load data. Please try again later.");
      if (!append) setCases([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!authLoaded || !accountIdsLoaded) return;
    fetchAlertsPage(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoaded, accountIdsLoaded, userType, accountIds]);

  // ========== AUTO‑SCROLL TO BOTTOM (unchanged) ==========
  useEffect(() => {
    if (!loading && alertsContainerRef.current) {
      alertsContainerRef.current.scrollTop = alertsContainerRef.current.scrollHeight;
    }
  }, [loading, cases]);

  // ========== PAGINATION — now fetched from backend instead of client slicing ==========
  const hasMoreCases = cases.length < totalCount;
  const handleReadMore = () => {
    if (loadingMore) return;
    fetchAlertsPage(cases.length, true);
  };

  // ========== TAB STYLES (unchanged) ==========
  const tabStyle = {
    padding: "8px 18px",
    marginRight: "5px",
    backgroundColor: "#f0f0f0",
    border: "1px solid #ccc",
    borderRadius: "5px 5px 0 0",
    textAlign: "center",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s ease",
    flex: "0 0 auto",
    whiteSpace: "nowrap",
  };
  const activeTabStyle = {
    ...tabStyle,
    backgroundColor: "#004aad",
    color: "white",
    borderBottom: "none",
  };

  const handleTabClick = (tab) => setSelectedTabs(tab);
  const handleDropdownChange = (event) => setSelectedOption(event.target.value);

  // ========== LOADING STATE ==========
  if (!authLoaded || !accountIdsLoaded) {
    return (
      <div className="homeright-wrapper" style={{ display: "flex", flexDirection: "column", marginBottom: "20px", width: "100%", overflow: "hidden", boxSizing: "border-box" }}>
        <div style={{ display: "flex", flexDirection: "column", borderRadius: "3px", padding: "10px 0", color: "black", marginTop: "10px", width: "100%", boxSizing: "border-box" }}>
          <div style={{ display: "flex", marginBottom: "15px", flexWrap: "nowrap" }}>
            <div style={activeTabStyle}>Alerts</div>
            <div style={tabStyle}>Activity</div>
          </div>
          <div style={{ textAlign: "center", padding: "20px" }}>
            <p>Loading user information...</p>
          </div>
        </div>
      </div>
    );
  }

  // ========== RENDER ==========
  return (
    <div className="homeright-wrapper" style={{
      display: "flex",
      flexDirection: "column",
      marginBottom: "0",
      width: "100%",
      overflow: "hidden",
      boxSizing: "border-box",
    }}>
      {/* horizontal padding 0 => content starts exactly at the left edge of the right column; vertical 10px keeps the original gap under the header
          (same left edge as the "Alerts & Activity" header) */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "3px",
        padding: "10px 0",
        color: "black",
        marginTop: "0",
        width: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
      }}>

        {/* ---- TABS ---- */}
        <div className="homeright-tabs" style={{
          display: "flex",
          marginBottom: "15px",
          flexWrap: "nowrap",
          gap: "4px",
          overflowX: "auto",
          width: "100%",
        }}>
          <div style={selectedTabs === "alerts" ? activeTabStyle : tabStyle} onClick={() => handleTabClick("alerts")}>
            Alerts
          </div>
          <div
  style={{
    ...tabStyle,
    backgroundColor: "#f5f5f5",
    color: "#999",
    borderColor: "#ddd",
    cursor: "not-allowed",
    opacity: 0.5,
    pointerEvents: "none",
    userSelect: "none",
  }}
  aria-disabled="true"
  title="Activity is disabled"
>
  Activity
</div>
        </div>

        {/* ---- CONTENT ---- */}
        <div style={{ width: "100%", overflow: "hidden" }}>

          {/* ========== ALERTS TAB ========== */}
          {selectedTabs === "alerts" && (
            <div className="alerts-content-wrapper" style={{ display: "flex", flexDirection: "column", width: "100%", overflow: "hidden" }}>
              <div
                className="homeright-table-container"
                ref={alertsContainerRef}
                style={{
                  overflowY: "auto",
                  maxHeight: "400px",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflowX: "hidden",
                }}
              >
                <table
                  style={{
                    textAlign: "left",
                    borderColor: "white",
                    tableLayout: "fixed",
                    width: "100%",
                    margin: "0 auto",
                    borderCollapse: "collapse",
                    flex: "1 1 auto",
                    minWidth: "0",
                  }}
                >
                  <thead style={{ position: "sticky", top: 0, zIndex: 5 }}>
                    <tr>
                      <th style={{ padding: "8px", backgroundColor: "#f8f9fa" }}>New Case Communications</th>
                    </tr>
                  </thead>
                  <tbody
                    className="alerts-tbody"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    {loading ? (
                      <tr style={{ display: "table-row", width: "100%" }}>
                        <td style={{ padding: "8px", width: "100%" }}>Loading {userType === 'lab' ? 'lab' : ''} cases...</td>
                      </tr>
                    ) : error ? (
                      <tr style={{ display: "table-row", width: "100%" }}>
                        <td style={{ padding: "8px", color: "red", width: "100%" }}>{error}</td>
                      </tr>
                    ) : cases.length === 0 ? (
                      <tr style={{ display: "table-row", width: "100%" }}>
                        <td style={{ padding: "8px", width: "100%" }}>No cases found.</td>
                      </tr>
                    ) : (
                      <div
                        className="alerts-list"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        {cases.map((caseItem) => {
                          const caseId = caseItem.case_id || caseItem._id || "N/A";
                          const accountName = caseItem.account_name || "Unknown Account";
                          const accountId = caseItem.accountId || "";
                          return (
                            <div
                              key={caseItem._id || caseId}
                              className="alert-row"
                              style={{
                                display: "table-row",
                                width: "100%",
                                borderBottom: "1px solid #eee",
                              }}
                            >
                              <div
                                style={{
                                  display: "table-cell",
                                  padding: "8px",
                                  width: "100%",
                                  wordBreak: "break-word",
                                }}
                              >
                                Case{" "}
                                <Link to={`/case/${caseId}`} style={{ textDecoration: "none", color: "#004aad" }}>
                                  #{caseId}
                                </Link>{" "}
                                created for{" "}
                                {accountId && caseItem.accountData ? (
                                  <Link
                                    to={`/TabsWorking/${accountId}`}
                                    state={{ accountName, accountData: caseItem.accountData }}
                                    style={{ textDecoration: "none", color: "#004aad" }}
                                  >
                                    {accountName}
                                  </Link>
                                ) : (
                                  <span>{accountName}</span>
                                )}{" "}
                                has a new internal note
                              </div>
                            </div>
                          );
                        })}
                        {hasMoreCases && (
                          <div
                            className="read-more-row"
                            style={{
                              display: "table-row",
                              width: "100%",
                            }}
                          >
                            <div
                              style={{
                                display: "table-cell",
                                padding: "12px 8px",
                                textAlign: "left",
                                width: "100%",
                              }}
                            >
                              <span
                                onClick={handleReadMore}
                                style={{
                                  color: "#004aad",
                                  textDecoration: "none",
                                  cursor: "pointer",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                  display: "inline-block",
                                }}
                                onMouseEnter={(e) => { e.target.style.textDecoration = "underline"; }}
                                onMouseLeave={(e) => { e.target.style.textDecoration = "none"; }}
                              >
                                {loadingMore ? "Loading..." : "Read more"}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        {/* ========== ACTIVITY TAB DISABLED ========== */}
          {false && selectedTabs === "activity" && (
            <div className="activity-content-wrapper" style={{ display: "flex", flexDirection: "column", padding: "15px", overflow: "hidden", width: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" }}>
                <label htmlFor="activityFilter" style={{ fontSize: "19px", fontWeight: "600", color: "#333", minWidth: "60px" }}>
                  View
                </label>
                <select
                  id="activityFilter"
                  value={selectedOption}
                  onChange={handleDropdownChange}
                  style={{
                    padding: "8px 12px",
                    width: "220px",
                    fontSize: "14px",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    backgroundColor: "#f8f8f8",
                    color: "#333",
                    outline: "none",
                    cursor: "pointer",
                  }}
                >
                  <option value="option1">Option 1</option>
                  <option value="option2">Option 2</option>
                  <option value="option3">Option 3</option>
                </select>
              </div>

              <div
                className="activity-list-container"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: "20px",
                  flex: "1 1 auto",
                  maxHeight: "300px",
                  overflowY: "auto",
                  width: "100%",
                  overflowX: "hidden",
                }}
              >
                <div
                  className="activity-list"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <div style={{ display: "table-row", width: "100%" }}>
                    <div style={{ display: "table-cell", padding: "8px", fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                      Today
                    </div>
                  </div>
                  <div style={{ display: "table-row", width: "100%" }}>
                    <div style={{ display: "table-cell", padding: "8px" }}>Activity #1: User logged in</div>
                  </div>
                  <div style={{ display: "table-row", width: "100%" }}>
                    <div style={{ display: "table-cell", padding: "8px" }}>Activity #2: User updated profile</div>
                  </div>
                  <div style={{ display: "table-row", width: "100%" }}>
                    <div style={{ display: "table-cell", padding: "8px" }}>Activity #3: New case created</div>
                  </div>
                  <div style={{ display: "table-row", width: "100%" }}>
                    <div style={{ display: "table-cell", padding: "8px" }}>Activity #4: Case status updated</div>
                  </div>
                </div>
              </div>

              <button
                style={{
                  height: "40px",
                  width: "80px",
                  marginTop: "10px",
                  backgroundColor: "#004aad",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  alignSelf: "flex-start",
                }}
              >
                Older
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ============================================================
          RESPONSIVE CSS (side padding of wrapper removed for alignment)
      ============================================================ */}
      <style>{`
        .homeright-wrapper { width: 100% !important; box-sizing: border-box !important; margin-top: 0 !important; padding-top: 0 !important; overflow: hidden !important; }
        .homeright-tabs { display: flex !important; flex-direction: row !important; flex-wrap: nowrap !important; gap: 4px !important; margin-bottom: 15px !important; overflow-x: auto !important; -webkit-overflow-scrolling: touch; scrollbar-width: none; width: 100% !important; }
        .homeright-tabs::-webkit-scrollbar { display: none; }
        .homeright-tabs > div { flex: 0 0 auto !important; white-space: nowrap !important; transition: all 0.2s ease !important; }
        .homeright-table-container { overflow-y: auto !important; max-height: 400px !important; width: 100% !important; display: flex !important; flex-direction: column !important; overflow-x: hidden !important; }
        .homeright-table-container table { width: 100% !important; min-width: 0 !important; table-layout: fixed !important; }
        .homeright-table-container table td, .homeright-table-container table th { word-break: break-word !important; overflow-wrap: break-word !important; }
        .activity-list-container { display: flex !important; flex-direction: column !important; max-height: 300px !important; overflow-y: auto !important; width: 100% !important; overflow-x: hidden !important; }
        .activity-list { display: flex !important; flex-direction: column !important; width: 100% !important; }
        .activity-list > div { display: table-row !important; width: 100% !important; }
        .activity-list > div > div { display: table-cell !important; padding: 8px !important; word-break: break-word !important; }

        @media (max-width: 992px) {
          .homeright-wrapper { padding: 0 !important; margin-top: 0 !important; }
          .homeright-tabs > div { padding: 6px 12px !important; font-size: 13px !important; border-radius: 4px 4px 0 0 !important; }
          .homeright-table-container table { font-size: 13px !important; min-width: 0 !important; }
          .homeright-table-container table td, .homeright-table-container table th { padding: 6px 8px !important; font-size: 13px !important; }
          .homeright-table-container table a { font-size: 13px !important; }
          .homeright-wrapper label { font-size: 16px !important; }
          .homeright-wrapper select { font-size: 13px !important; padding: 6px 10px !important; width: 180px !important; }
          .homeright-wrapper button { height: 35px !important; width: 70px !important; font-size: 13px !important; }
          .alerts-list { display: flex !important; flex-direction: column-reverse !important; width: 100% !important; }
          .alert-row { display: table-row !important; width: 100% !important; }
          .alert-row > div { display: table-cell !important; padding: 6px 8px !important; width: 100% !important; font-size: 13px !important; word-break: break-word !important; }
          .read-more-row { display: table-row !important; width: 100% !important; }
          .read-more-row > div { display: table-cell !important; padding: 10px 8px !important; width: 100% !important; }
          .activity-list { display: flex !important; flex-direction: column-reverse !important; width: 100% !important; }
          .activity-list > div { display: table-row !important; width: 100% !important; }
          .activity-list > div > div { display: table-cell !important; padding: 6px 8px !important; font-size: 13px !important; }
        }

        @media (max-width: 768px) {
          .homeright-wrapper { padding: 0 !important; margin-top: 0 !important; }
          .homeright-tabs { gap: 3px !important; }
          .homeright-tabs > div { padding: 5px 10px !important; font-size: 12px !important; border-radius: 3px 3px 0 0 !important; }
          .homeright-table-container table { font-size: 12px !important; min-width: 0 !important; }
          .homeright-table-container table td, .homeright-table-container table th { padding: 5px 6px !important; font-size: 12px !important; }
          .homeright-table-container table a { font-size: 12px !important; }
          .homeright-wrapper label { font-size: 14px !important; min-width: 50px !important; }
          .homeright-wrapper select { font-size: 12px !important; padding: 5px 8px !important; width: 150px !important; }
          .homeright-wrapper button { height: 32px !important; width: 60px !important; font-size: 12px !important; }
          .alerts-list { display: flex !important; flex-direction: column-reverse !important; width: 100% !important; }
          .alert-row { display: table-row !important; width: 100% !important; }
          .alert-row > div { display: table-cell !important; padding: 5px 6px !important; width: 100% !important; font-size: 12px !important; word-break: break-word !important; }
          .read-more-row { display: table-row !important; width: 100% !important; }
          .read-more-row > div { display: table-cell !important; padding: 8px 6px !important; width: 100% !important; }
          .read-more-row > div span { font-size: 12px !important; }
          .activity-list { display: flex !important; flex-direction: column-reverse !important; width: 100% !important; }
          .activity-list > div { display: table-row !important; width: 100% !important; }
          .activity-list > div > div { display: table-cell !important; padding: 5px 6px !important; font-size: 12px !important; }
        }

        @media (max-width: 576px) {
          .homeright-wrapper { padding: 0 !important; margin-top: 0 !important; }
          .homeright-tabs { gap: 2px !important; margin-bottom: 10px !important; }
          .homeright-tabs > div { padding: 4px 8px !important; font-size: 10px !important; border-radius: 3px 3px 0 0 !important; margin-right: 2px !important; }
          .homeright-table-container table { font-size: 10px !important; min-width: 0 !important; }
          .homeright-table-container table td, .homeright-table-container table th { padding: 4px 5px !important; font-size: 10px !important; }
          .homeright-table-container table a { font-size: 10px !important; }
          .homeright-wrapper label { font-size: 12px !important; min-width: 40px !important; }
          .homeright-wrapper select { font-size: 10px !important; padding: 4px 6px !important; width: 120px !important; }
          .homeright-wrapper button { height: 28px !important; width: 50px !important; font-size: 10px !important; border-radius: 3px !important; }
          .homeright-wrapper .homeright-tabs + div { padding: 5px !important; }
          .homeright-table-container span { font-size: 10px !important; }
          .homeright-wrapper .activity-content-wrapper { padding: 5px !important; }
          .alerts-list { display: flex !important; flex-direction: column-reverse !important; width: 100% !important; }
          .alert-row { display: table-row !important; width: 100% !important; }
          .alert-row > div { display: table-cell !important; padding: 4px 5px !important; width: 100% !important; font-size: 10px !important; word-break: break-word !important; }
          .read-more-row { display: table-row !important; width: 100% !important; }
          .read-more-row > div { display: table-cell !important; padding: 6px 5px !important; width: 100% !important; }
          .read-more-row > div span { font-size: 10px !important; }
          .activity-list { display: flex !important; flex-direction: column-reverse !important; width: 100% !important; }
          .activity-list > div { display: table-row !important; width: 100% !important; }
          .activity-list > div > div { display: table-cell !important; padding: 4px 5px !important; font-size: 10px !important; word-break: break-word !important; }
        }

        @media (max-width: 400px) {
          .homeright-wrapper { margin-top: 0 !important; }
          .homeright-tabs > div { padding: 3px 6px !important; font-size: 8px !important; border-radius: 2px 2px 0 0 !important; }
          .homeright-table-container table td, .homeright-table-container table th { padding: 3px 4px !important; font-size: 8px !important; }
          .homeright-table-container table a { font-size: 8px !important; }
          .homeright-wrapper label { font-size: 10px !important; min-width: 35px !important; }
          .homeright-wrapper select { font-size: 9px !important; padding: 3px 5px !important; width: 100px !important; }
          .homeright-wrapper button { height: 24px !important; width: 45px !important; font-size: 9px !important; }
          .homeright-table-container span { font-size: 9px !important; }
          .alert-row > div { font-size: 8px !important; padding: 3px 4px !important; }
          .read-more-row > div span { font-size: 9px !important; }
          .activity-list > div > div { font-size: 8px !important; padding: 3px 4px !important; }
        }
      `}</style>
    </div>
  );
};

export default Homeright;